import api from "./api";
import axios from "axios";

export type SunbedWaitingListStatus = "waiting" | "called" | "started" | "cancelled";

export type SunbedWaitingListItem = {
  id: number;
  queue_position: number;
  user_id: number;
  package_id?: number | null;
  membership_id?: number | null;
  source?: "prepaid" | "membership" | string;
  customer: string;
  customer_phone?: string;
  package_name: string;
  /** Catalog sunbed id for the prepaid package (room filtering). */
  sunbed_id?: number | null;
  duration_minutes: number;
  delay_minutes: number;
  preferred_room_id?: number | null;
  preferred_room_label?: string | null;
  status: SunbedWaitingListStatus | string;
  created_at: string;
  waited_formatted?: string;
  notes?: string | null;
};

export type AddSunbedWaitingListPayload = {
  user_id: number;
  /** Prepaid customer package row id (`sunbed_packages.id`). */
  package_id?: number;
  /** Alias for package_id — kept for older callers. */
  sunbed_package_id?: number;
  /** Active sunbed membership id. */
  membership_id?: number;
  duration_minutes: number;
  delay_minutes?: number;
  preferred_room_id?: number | null;
  notes?: string | null;
};

function buildStoreBody(payload: AddSunbedWaitingListPayload): Record<string, unknown> {
  const roomId = payload.preferred_room_id ?? null;
  const body: Record<string, unknown> = {
    user_id: payload.user_id,
    duration_minutes: payload.duration_minutes,
    delay_minutes: payload.delay_minutes ?? 0,
    preferred_room_id: roomId,
    sunbed_room_id: roomId,
    notes: payload.notes ?? null,
  };

  if (payload.membership_id) {
    body.membership_id = payload.membership_id;
    body.sunbed_membership_id = payload.membership_id;
  } else {
    const prepaidPackageId = payload.package_id || payload.sunbed_package_id;
    body.package_id = prepaidPackageId;
    body.sunbed_package_id = prepaidPackageId;
  }

  return body;
}

export type StartSunbedWaitingListPayload = {
  room_id: number;
};

export type StartSunbedWaitingListResult = {
  message?: string;
  waiting_list_id?: number;
  session_id?: number;
};

function readNestedLabel(obj: Record<string, unknown> | undefined): string | null {
  if (!obj) return null;
  if (typeof obj.label === "string" && obj.label) return obj.label;
  if (typeof obj.name === "string" && obj.name) return obj.name;
  if (typeof obj.room_name === "string" && obj.room_name) return obj.room_name;
  return null;
}

function mapWaitingListRow(raw: Record<string, unknown>): SunbedWaitingListItem {
  const user = raw.user as Record<string, unknown> | undefined;
  const packageRelation = raw.package as Record<string, unknown> | undefined;
  const membershipRelation = raw.membership as Record<string, unknown> | undefined;
  const membershipPlan = membershipRelation?.plan as Record<string, unknown> | undefined;
  const sunbedFromPackage = packageRelation?.sunbed as Record<string, unknown> | undefined;
  const preferredRoom = raw.preferred_room as Record<string, unknown> | undefined;
  const sunbedRoom = raw.sunbed_room as Record<string, unknown> | undefined;
  const room = raw.room as Record<string, unknown> | undefined;
  const sunbedPackage = raw.sunbed_package as Record<string, unknown> | undefined;

  const membershipId =
    raw.membership_id != null
      ? Number(raw.membership_id)
      : raw.sunbed_membership_id != null
        ? Number(raw.sunbed_membership_id)
        : membershipRelation?.id != null
          ? Number(membershipRelation.id)
          : null;

  const source =
    (typeof raw.source === "string" && raw.source) ||
    (membershipId ? "membership" : "prepaid");

  const customerName =
    (typeof raw.customer === "string" && raw.customer) ||
    (typeof user?.name === "string" && user.name) ||
    "—";

  const packageName =
    (typeof raw.package_name === "string" && raw.package_name) ||
    (typeof membershipPlan?.name === "string" && membershipPlan.name) ||
    (typeof sunbedFromPackage?.label === "string" && sunbedFromPackage.label) ||
    (typeof packageRelation?.package_name === "string" && packageRelation.package_name) ||
    (typeof sunbedPackage?.label === "string" && sunbedPackage.label) ||
    (typeof sunbedPackage?.package_name === "string" && sunbedPackage.package_name) ||
    (source === "membership" ? "Membership" : "—");

  const packageBookingId =
    raw.package_id ??
    packageRelation?.id ??
    raw.sunbed_package_id ??
    null;

  const sunbedId =
    raw.sunbed_id != null
      ? Number(raw.sunbed_id)
      : sunbedFromPackage?.id != null
        ? Number(sunbedFromPackage.id)
        : packageRelation?.sunbed_id != null
          ? Number(packageRelation.sunbed_id)
          : null;

  return {
    id: Number(raw.id),
    queue_position: Number(raw.queue_position ?? raw.position ?? 0),
    user_id: Number(raw.user_id ?? user?.id ?? 0),
    package_id: packageBookingId != null ? Number(packageBookingId) : null,
    membership_id: membershipId,
    source,
    customer: customerName,
    customer_phone:
      (typeof raw.customer_phone === "string" && raw.customer_phone) ||
      (typeof user?.mobile === "string" && user.mobile) ||
      (typeof user?.formatted_mobile === "string" && user.formatted_mobile) ||
      undefined,
    package_name: packageName,
    sunbed_id: sunbedId,
    duration_minutes: Number(raw.duration_minutes ?? raw.interval ?? 0),
    delay_minutes: Number(raw.delay_minutes ?? raw.delay ?? 0),
    preferred_room_id:
      raw.preferred_room_id != null
        ? Number(raw.preferred_room_id)
        : raw.sunbed_room_id != null
          ? Number(raw.sunbed_room_id)
          : room?.id != null
            ? Number(room.id)
            : sunbedRoom?.id != null
              ? Number(sunbedRoom.id)
              : preferredRoom?.id != null
                ? Number(preferredRoom.id)
                : null,
    preferred_room_label:
      readNestedLabel(room) ||
      readNestedLabel(sunbedRoom) ||
      readNestedLabel(preferredRoom) ||
      (typeof raw.preferred_room_label === "string" && raw.preferred_room_label) ||
      (typeof raw.room_label === "string" && raw.room_label) ||
      null,
    status: String(raw.status ?? "waiting"),
    created_at: String(raw.created_at ?? ""),
    waited_formatted:
      typeof raw.waited_formatted === "string" ? raw.waited_formatted : undefined,
    notes: typeof raw.notes === "string" ? raw.notes : null,
  };
}

function extractWaitingList(data: unknown): SunbedWaitingListItem[] {
  if (!data || typeof data !== "object") return [];
  const root = data as Record<string, unknown>;
  const list = Array.isArray(root.data)
    ? root.data
    : Array.isArray(root.waiting_list)
      ? root.waiting_list
      : Array.isArray(root)
        ? root
        : [];
  return list
    .filter((item): item is Record<string, unknown> => item != null && typeof item === "object")
    .map(mapWaitingListRow);
}

export async function getSunbedWaitingList(): Promise<SunbedWaitingListItem[]> {
  const response = await api.get("/admin/sunbed-waiting-list", {
    headers: { Accept: "application/json" },
  });
  return extractWaitingList(response.data);
}

export async function addSunbedWaitingListEntry(
  payload: AddSunbedWaitingListPayload
): Promise<SunbedWaitingListItem> {
  const response = await api.post("/admin/sunbed-waiting-list", buildStoreBody(payload), {
    headers: { Accept: "application/json" },
  });
  const rows = extractWaitingList(response.data);
  if (rows[0]) return rows[0];
  const data = response.data as Record<string, unknown>;
  const single = data?.data;
  if (single && typeof single === "object") {
    return mapWaitingListRow(single as Record<string, unknown>);
  }
  throw new Error("No waiting list entry returned from server.");
}

export async function cancelSunbedWaitingListEntry(id: number | string): Promise<void> {
  await api.post(`/admin/sunbed-waiting-list/${id}/cancel`, {}, {
    headers: { Accept: "application/json" },
  });
}

export async function startSunbedWaitingListEntry(
  id: number | string,
  payload: StartSunbedWaitingListPayload
): Promise<StartSunbedWaitingListResult> {
  const response = await api.post(`/admin/sunbed-waiting-list/${id}/start`, payload, {
    headers: { Accept: "application/json" },
  });
  const data = response.data as Record<string, unknown>;
  const inner = data?.data as Record<string, unknown> | undefined;
  return {
    message: typeof data.message === "string" ? data.message : undefined,
    waiting_list_id:
      inner?.waiting_list_id != null ? Number(inner.waiting_list_id) : undefined,
    session_id: inner?.session_id != null ? Number(inner.session_id) : undefined,
  };
}

export function getSunbedWaitingListErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err) && err.response?.data) {
    const d = err.response.data as Record<string, unknown>;
    if (typeof d.message === "string") return d.message;
    const errors = d.errors;
    if (errors && typeof errors === "object") {
      const first = Object.values(errors as Record<string, unknown>)[0];
      if (Array.isArray(first) && typeof first[0] === "string") return first[0];
      if (typeof first === "string") return first;
    }
    const nested = d.error;
    if (nested && typeof nested === "object" && typeof (nested as Record<string, unknown>).message === "string") {
      return (nested as Record<string, unknown>).message as string;
    }
  }
  if (err instanceof Error) return err.message;
  return "Could not complete waiting list request.";
}
